import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { CryptoHelper } from '../../encription/encription';

interface AuthContextProps {
    token: string | null;
    employeeId: number | null;
    firstName: string | null;
    setAuthData: (data: { token: string; employeeId: number; firstName: string }) => void;
    updateToken: (newToken: string) => void;
    clearAuthData: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [authState, setAuthState] = useState<{
        token: string | null;
        employeeId: number | null;
        firstName: string | null;
    }>({
        token: null,
        employeeId: null,
        firstName: null,
    });

    // Restore data from cookies on app load
    useEffect(() => {
        const storedAuthData = Cookies.get('data_user');
        const decryptedData = storedAuthData ? CryptoHelper.decryptData(storedAuthData) : null;
        if (decryptedData) {
            try {
                const parsedData = JSON.parse(decryptedData);
                setAuthState({
                    token: parsedData.token || null,
                    employeeId: parsedData.employeeId || null,
                    firstName: parsedData.firstName || null,
                });
            } catch (error) {
                console.error('Error parsing decrypted auth data:', error);
            }
        }
    }, []);

    const setAuthData = ({ token, employeeId, firstName }: { token: string; employeeId: number; firstName: string }) => {
        const newAuthState = { token, employeeId, firstName };
        setAuthState(newAuthState);
        Cookies.set('data_user', CryptoHelper.encryptData(JSON.stringify(newAuthState)), { expires: 1 }); // persist for 24 hours
    };

    const updateToken = (newToken: string) => {
        setAuthState((prev) => {
            const updatedState = { ...prev, token: newToken };
            Cookies.set('data_user', CryptoHelper.encryptData(JSON.stringify(updatedState)), { expires: 1 });
            return updatedState;
        });
    };

    const clearAuthData = () => {
        setAuthState({ token: null, employeeId: null, firstName: null });
        Cookies.remove('data_user');
    };

    return (
        <AuthContext.Provider value={{ ...authState, setAuthData, updateToken, clearAuthData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextProps => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};